import { useQuery } from "@tanstack/react-query"
import * as Effect from "effect/Effect"
import { pipe } from "effect/Function"
import { ApiClient } from "@/api/client"
import { MainLayer } from "@/effects/layers"
import { runEffect } from "@/effects/runtime"
import { z } from "zod"
import { logger } from '@/lib/logger'

// Schema for debt calculation response
const DebtSchema = z.object({
  from_user_id: z.string(),
  to_user_id: z.string(),
  amount: z.number(),
  from_user_name: z.string(),
  to_user_name: z.string(),
})

const DebtsResponseSchema = z.object({
  debts: z.array(DebtSchema),
})

export type Debt = z.infer<typeof DebtSchema>
export type DebtsResponse = z.infer<typeof DebtsResponseSchema>

// Hook to get debts between users in a group
export const useUserDebts = (groupId: string | null) => {
  logger.debug("useUserDebts hook called with groupId:", { groupId })
  return useQuery({
    queryKey: ["user-debts", groupId],
    queryFn: async () => {
      logger.debug("Fetching debts for group:", { groupId })
      if (!groupId) return { debts: [] }

      const program = pipe(
        Effect.gen(function* () {
          const apiClient = yield* ApiClient
          const url = `/groups/${groupId}/debts`
          logger.debug("Making API call to:", { url })

          const response = yield* apiClient.get(url, DebtsResponseSchema)
          logger.debug("Debts API response:", { response })
          return response
        }),
        Effect.catchAll((error) => {
          logger.error("Failed to fetch user debts:", { error })
          return Effect.succeed({ debts: [] })
        }),
        Effect.provide(MainLayer)
      )

      return runEffect(program)
    },
    enabled: !!groupId,
  })
}